import React, { useContext, useMemo } from 'react';
import CardSlot from '@/pages/dashboard/components/CardSlot';
import "./index.scss";
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { useIntl, useSelector } from 'umi';
import TextBubbles from '@/pages/home/components/TextBubbles';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getSystemFlag } from '@/pages/dashboard/pages/overview/_config/system';
import { formatExeclCommon, formatExeclSharePercent, jumpDetail, formatExeclSharePercentDecimal, formatNum, formatPercentNum, handleDownload } from '@/pages/dashboard/utils/utils';
import { LANGUAGE_EN } from '@/pages/dashboard/_config/constant';
import { usePcRevenueSelectorMap } from '@/pages/dashboard/filterPageComponents/monetization/hooks';
import { replacePlaceholders } from './utils';
import { ISalesBreakdownCardProps } from './type';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import SvgIcon from '@home/components/SvgIcon';
import classNames from 'classnames';

const SalesBreakdownCard: React.FC<ISalesBreakdownCardProps> = (props) => {
  const { selectedMetrics, data, config } = props || {};
  const {
    title,
    titleCn,
    subTitle,
    subTitleCn,
    firstWidth = 160,
    breakdownType,
    metricsConfig,
    jumpUrl,
    jumpDefaultAccumulation: defaultAccumulation,
    jumpOneTimeDefaultSortOrder: oneTimeDefaultSortOrder,
  } = config || {};

  const { filterVal } = useContext(FilterContext);
  const { selector, timeType } = filterVal || {};
  const { getPcRevenueSelectorText } = usePcRevenueSelectorMap(selector);
  const { locale, formatMessage } = useIntl();
  const gameCode = useSelector((state: any) => (state?.dashboard?.currentGame?.game_code));

  const { breakdown_data: breakdownData } = data || {};

  const isEn = locale === LANGUAGE_EN;

  const { isPercent } = selectedMetrics;
  const { isNoSum = false, isNoDistributionRatio = false, decimal } = metricsConfig?.[selectedMetrics?.id] || {};
  const breakdownList = breakdownData?.data?.dwm_list || [];
  const isError = !!breakdownData?.errorMsg;
  const isLoading = breakdownData?.loading;

  const cardTitle = isEn ?
    replacePlaceholders(
      title,
      {
        metric: selectedMetrics.name
      }
    ) : replacePlaceholders(
      titleCn,
      {
        metric: selectedMetrics.nameCn
      }
    );
  // list, // to breakdownList
  // listHeader,
  const footerFixed = breakdownList.length === 11;
  // type: breakdownType,

  let rightSubTitle = isEn ? selectedMetrics.name : selectedMetrics.nameCn;

  if (isNoSum) {
    rightSubTitle = rightSubTitle;
  } else if (isEn) {
    rightSubTitle = replacePlaceholders('SUM {{metric}}', { metric: selectedMetrics.name });
  } else {
    rightSubTitle = replacePlaceholders('{{metric}}汇总', { metric: selectedMetrics.nameCn });
  }

  const listHeader = [
    isEn
      ? subTitle
      : subTitleCn,
    rightSubTitle
  ];

  const list = useMemo(() => {
    const newData = breakdownList?.length >= 11
      ? breakdownList
      : breakdownList?.filter((item: any) => !(item?.name?.toLowerCase() === 'others' && item?.value === ''));

    const filteredList = newData?.map((item: any) => {
      if (parseFloat(item?.value) !== 0) {
        let icon = '';

        if (item?.name?.toLowerCase() !== 'others') {
          if (breakdownType === 'country') {
            icon = getCountryFlag(item?.name);
          } else if (breakdownType === 'os') {
            icon = getSystemFlag(item?.full_name);
          }
        }

        // TOFIX: 暂时处理，负数百分比不需要处理成正数
        const percentage = `${parseFloat(item?.share_of_all?.trim()) < 0 ? '-' : ''}${formatPercentNum(parseFloat(item?.share_of_all?.trim()), 1)}`;

        const isMinus = parseFloat(item?.value) < 0;
        let num = `${isMinus ? '-' : ''}`;
        if (isPercent) {
          num += `${formatPercentNum(parseFloat(item?.value), decimal || 1)}`;
        } else {
          num += `${formatNum(isMinus ? Math.abs(item?.value) : item.value, false, selectedMetrics?.isUserType)}`;
        }

        return {
          ...item,
          icon,
          name: getPcRevenueSelectorText(breakdownType, item?.full_name),
          num,
          percentage,
        };
      }
      return null;
    }).filter(Boolean);

    return filteredList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownList, breakdownType, locale]);

  const onDownload = () => {
    const tableOption = [
      {
        key: 'name',
        align: 'left',
        header: isEn ? subTitle : subTitleCn,
      },
      {
        key: 'value',
        align: 'right',
        header: isEn ? selectedMetrics.name : selectedMetrics.nameCn,
      },
      (!(isPercent || isNoDistributionRatio) && {
        key: 'share_of_all',
        align: 'right',
        header: formatMessage({ id: 'common_share' }),
      })
    ].filter(Boolean);
    const newData = breakdownList?.length >= 11 ? breakdownList : breakdownList?.filter((v: any) => !((v?.name?.toLowerCase() === 'others' && v?.value === '') || (parseFloat(v?.value) === 0)));
    const downloadData: any = [];
    newData?.map((item: any) => {
      const downObj: any = {};
      downObj.name = getPcRevenueSelectorText(breakdownType, item?.full_name);
      downObj.value = isPercent ? formatExeclSharePercentDecimal(item?.value, decimal) : formatExeclCommon(item?.value);
      downObj.share_of_all = formatExeclSharePercent(item?.share_of_all);
      downloadData?.push(downObj);

      return null;
    });
    handleDownload({
      option: tableOption,
      downloadData,
    }, cardTitle, false);
  };

  const isNoData = list.length === 0;

  const jumpToLink = () => {
    if (jumpUrl) {
      const formValue = {
        filterValue: filterVal,
        timeType,
        diff: filterVal?.diff
      };
      jumpDetail(`/dashboard/game/${gameCode}${jumpUrl}`,
        formValue,
        selectedMetrics?.id,
        { defaultAccumulation, oneTimeDefaultSort: [{ by: selectedMetrics?.id, order: oneTimeDefaultSortOrder || 'desc' }] });
    }
  };

  return (
    <CardSlot
      title={<div className={classNames(
        "dbd-sales-breakdown-card-title",
        { "dbd-sales-breakdown-card-title-jump": !!jumpUrl }
      )} onClick={jumpToLink}>
        {cardTitle}
        {
          jumpUrl && <span className='dbl-region-title-jump'>
            <SvgIcon className='dbd-line-title-icon' type='arrow-right'></SvgIcon>
          </span>
        }
      </div>
      }
      isEyes={true}
      className="dbd-sales-breakdown-card  dbd-sales-breakdown-card-jump"
      onDownload={onDownload}
      iconShow={isLoading || isError || isNoData}
    >
      <Abnormal
        isLoading={isLoading}
        style={{ height: '266px' }}
        isError={isError}
        isNoData={isNoData}
        component={
          <>
            <div className="dbd-sales-breakdown-card-sub-title">
              {listHeader?.map((e: string, index) => <span key={index}>{e}</span>)}
            </div>

            <div className={`dbd-sales-breakdown-card-body ${footerFixed ? 'footer-fixed' : null}`}>
              {list.map((item: any, index: number) => (
                <div key={index} className="dbd-sales-breakdown-flex">
                  <div
                    className="dbd-sales-breakdown-flex-left"
                    style={{ width: firstWidth ? firstWidth : undefined }}
                  >
                    {item?.icon ? (
                      <img
                        src={item?.icon}
                        className={
                          breakdownType === 'country'
                            ? 'dbd-sales-breakdown-flex-country-img'
                            : 'dbd-sales-breakdown-flex-img'
                        }
                      />
                    ) : null}
                    <TextBubbles className="dbd-sales-breakdown-flex-name" text={item?.name} />
                  </div>
                  <div className="dbd-sales-breakdown-flex-center">
                    <div
                      className="dbd-sales-breakdown-flex-show"
                      // 百分比类型指标，占比显示其数值，其他类型显示其占比
                      // eslint-disable-next-line no-nested-ternary
                      style={{ width: isPercent ? item?.num : (isNoDistributionRatio) ? '0%' : item?.percentage }}
                    />
                  </div>
                  <div className="dbd-sales-breakdown-flex-right">
                    {item?.num}
                    {!(isPercent || isNoDistributionRatio) && ` (${item?.percentage})`}

                  </div>
                </div>
              ))}
            </div>
          </>
        } />

    </CardSlot>
  );
};

export default SalesBreakdownCard;